import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validatePassword, PASSWORD_HINT } from '../passwordPolicy';
import PasswordInput from '../components/PasswordInput';

export default function ChangePassword() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forced = searchParams.get('forced') === 'true';

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword)
      return setError('All fields are required');
    if (form.newPassword !== form.confirmPassword)
      return setError('New passwords do not match');
    const passwordError = validatePassword(form.newPassword);
    if (passwordError) return setError(passwordError);
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      setSuccess('Password changed successfully!');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      const destination = sessionStorage.getItem('superadmin') === 'true' ? '/superadmin/dashboard' : '/players';
      setTimeout(() => navigate(destination), 2000);
    } catch(e) {
      setError(e.response?.data?.error || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const isSuperAdmin = sessionStorage.getItem('superadmin') === 'true';

  const form_ = (
    <div className={isSuperAdmin ? "p-8 rounded-xl shadow-md w-full max-w-md" : "bg-white p-8 rounded-xl shadow-md w-full max-w-md"}
      style={isSuperAdmin ? { background: '#312e81' } : undefined}>
      <h2 className={isSuperAdmin ? "text-2xl font-bold text-white mb-6" : "text-2xl font-bold text-green-800 mb-6"}>
        {isSuperAdmin ? '🔑 System Administration' : 'Change Password'}
      </h2>
      {forced && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-4 text-sm">
          For account security, you need to set a stronger password before continuing.
        </div>
      )}
      {error && <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-100 text-green-700 px-4 py-2 rounded mb-4 text-sm">{success}</div>}
      <div className="space-y-4">
        <div>
          <label className={isSuperAdmin ? "block text-sm font-medium text-indigo-200 mb-1" : "block text-sm font-medium text-gray-700 mb-1"}>Current Password</label>
          <PasswordInput value={form.currentPassword}
            onChange={e => setForm({...form, currentPassword: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <div>
          <label className={isSuperAdmin ? "block text-sm font-medium text-indigo-200 mb-1" : "block text-sm font-medium text-gray-700 mb-1"}>New Password</label>
          <PasswordInput value={form.newPassword}
            onChange={e => setForm({...form, newPassword: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          <p className={isSuperAdmin ? "text-sm font-medium text-indigo-200 mt-1" : "text-sm font-medium text-gray-600 mt-1"}>{PASSWORD_HINT}</p>
        </div>
        <div>
          <label className={isSuperAdmin ? "block text-sm font-medium text-indigo-200 mb-1" : "block text-sm font-medium text-gray-700 mb-1"}>Confirm New Password</label>
          <PasswordInput value={form.confirmPassword}
            onChange={e => setForm({...form, confirmPassword: e.target.value})}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <button onClick={handleSubmit} disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition text-sm">
          {loading ? 'Changing...' : 'Change Password'}
        </button>
        {!forced && (
          <button onClick={() => navigate(isSuperAdmin ? '/superadmin/dashboard' : '/players')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition text-sm">
            Cancel
          </button>
        )}
      </div>
    </div>
  );

  // Triton42 is the system superadmin account, not a real golf society — the
  // normal Layout wrapper renders a whole club-management sidebar (Players,
  // Groups, Teams...) around it, which looks like a completely different,
  // broken app rather than "System Administration". Render standalone,
  // matching SuperAdminLogin's own styling, instead.
  if (isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1e1b4b' }}>
        {form_}
      </div>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-center h-full bg-gray-50">
        {form_}
      </div>
    </Layout>
  );
}
